import React, { useState, useEffect, useRef } from "react";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import InputField from "components/common/Input/Input";

const MAX_NAME_LENGTH = 150;

const CreateCategoryModal = ({ isOpen, onClose, onSubmit, isLoading = false }) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const submittingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setNameError("");
      submittingRef.current = false;
      if (typeof window !== "undefined") window.__createCategorySubmitLock = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isLoading) {
      submittingRef.current = false;
      if (typeof window !== "undefined") window.__createCategorySubmitLock = false;
    }
  }, [isLoading]);

  // Module-level lock: only one submit in the entire app at a time (same pattern as Add Document)
  const runSubmit = () => {
    if (typeof window !== "undefined" && window.__createCategorySubmitLock) return;
    if (typeof window !== "undefined") window.__createCategorySubmitLock = true;
    if (submittingRef.current) {
      if (typeof window !== "undefined") window.__createCategorySubmitLock = false;
      return;
    }
    setNameError("");
    if (!name.trim()) {
      setNameError("Category name is required.");
      if (typeof window !== "undefined") window.__createCategorySubmitLock = false;
      return;
    }
    if (name.length > MAX_NAME_LENGTH) {
      setNameError(`Category name must not exceed ${MAX_NAME_LENGTH} characters.`);
      if (typeof window !== "undefined") window.__createCategorySubmitLock = false;
      return;
    }
    submittingRef.current = true;
    if (isLoading) {
      submittingRef.current = false;
      if (typeof window !== "undefined") window.__createCategorySubmitLock = false;
      return;
    }
    onSubmit({ name: name.trim() });
  };

  const handleClose = () => {
    if (!isLoading) {
      setName("");
      setNameError("");
      onClose();
    }
  };

  // Single path: form only preventDefault; Enter via onKeyDown; button type="button" + clickHandler only.
  return (
    <ModalSkeleton
      isOpen={isOpen}
      title="Create New Category"
      closeModal={handleClose}
      modalFooter={null}
      cssClass="w-full max-w-md"
    >
      <form
        onSubmit={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            runSubmit();
          }
        }}
        className="space-y-5"
      >
        <div>
          <InputField
            name="category_name"
            type="text"
            label="Category Name"
            redstar
            placeHolder="e.g. Training Certificates"
            value={name}
            handleChange={(val) => {
              setName(val);
              if (nameError && val.length < MAX_NAME_LENGTH) setNameError("");
            }}
            maxLength={MAX_NAME_LENGTH}
            ErrorMessage={nameError || (name.length >= MAX_NAME_LENGTH ? "Maximum character limit reached." : "")}
          />
          <span
            className="mt-0.5 block"
            style={
              name.length >= MAX_NAME_LENGTH
                ? { fontSize: "13px", color: "red", marginLeft: "5px" }
                : { fontSize: "12px", color: "#6b7280" }
            }
          >
            {name.length}/{MAX_NAME_LENGTH}
          </span>
        </div>
        <div className="flex justify-end gap-x-2 pt-2 border-t border-gray-200 mt-4">
          <CustomButton
            type="button"
            variant="secondary"
            label="Cancel"
            clickHandler={handleClose}
            isDisabled={isLoading}
          />
          <CustomButton
            type="button"
            variant="primary"
            label="Create Category"
            clickHandler={(e) => {
              e.preventDefault();
              runSubmit();
            }}
            showLoading={isLoading}
            isDisabled={isLoading || !name.trim() || name.length > MAX_NAME_LENGTH}
          />
        </div>
      </form>
    </ModalSkeleton>
  );
};

export default CreateCategoryModal;
